import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Menu, Users, User, ChevronDown, ChevronUp, Moon, Sun, UserPlus, LogIn } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import AuthModal from './AuthModal';

const Header = ({ onBookNow, onOpenAdmin, onOpenClientPortal, onCustomerLogin }) => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCeoModalOpen, setIsCeoModalOpen] = useState(false);
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  
  const handleAuthSuccess = (userData) => {
    // Pass login data to parent component
    if (onCustomerLogin) {
      onCustomerLogin(userData);
    }
    // Don't close the modal - let AuthModal show the dashboard instead
  };

  const ceoInfo = {
    name: "Peter Gwaze",
    title: "Founder & CEO",
    photo: "/peter-gwaze.jpg",
    shortBio: "Peter Gwaze is the Founder and CEO of NEURA AI SOFTWARES and a fully certified Telecommunications Technician. He leads a cutting-edge artificial intelligence consultancy dedicated to helping businesses harness the power of AI to drive innovation, efficiency, and growth.",
    fullBio: "Peter Gwaze is the Founder and CEO of NEURA AI SOFTWARES and a fully certified Telecommunications Technician. He leads a cutting-edge artificial intelligence consultancy dedicated to helping businesses harness the power of AI to drive innovation, efficiency, and growth. With a deep passion for technology and a visionary approach to business transformation, Peter founded NEURA AI SOFTWARES to bridge the gap between complex AI solutions and practical, real-world applications for enterprises of all sizes."
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Growth Engine', href: '#packages' },
    { label: 'Industries', href: '#solutions' },
    { label: 'About', href: '#about' },
    { label: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    setTimeout(() => {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-white shadow-sm" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 bg-white">
        <div className="flex items-start justify-between pt-3 pb-2">
          {/* Logo, Company Name, and CEO Section */}
          <div className="flex flex-col flex-shrink-0">
            {/* Logo and Company Name Row */}
            <a href="#" className="flex items-center gap-3 group" data-testid="logo">
              <div className="relative">
                <img 
                  src="/neura-logo.png" 
                  alt="NEURA AI SOFTWARES" 
                  className="h-10 sm:h-12 w-auto object-contain"
                />
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 blur-2xl opacity-10 group-hover:opacity-20 transition-opacity" />
              </div>
              <div className="flex flex-col">
                <span className="text-lg sm:text-xl font-bold tracking-wide">
                  <span className="bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">NEURA</span>
                  <span className="text-gray-800"> AI</span>
                </span>
                <span className="text-[8px] sm:text-[10px] text-gray-500 tracking-[0.15em] font-medium">SOFTWARES</span>
              </div>
            </a>
            
            {/* CEO Full Image Section - Below Company Name - Visible on all screens */}
            {/* CEO profile moved to Footer */}
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-2 bg-white pt-2">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.href)}
                className={`dynamic-button px-4 py-2 rounded-lg text-white font-medium text-sm transition-all duration-300 shadow-md hover:shadow-lg ${
                  item.label === 'Industries' 
                    ? 'bg-gradient-to-r from-rose-600 to-red-700 hover:from-rose-700 hover:to-red-800 shadow-rose-500/20 hover:shadow-rose-500/30'
                    : 'bg-gradient-to-r from-orange-400 to-amber-500 hover:from-orange-500 hover:to-amber-600 shadow-orange-500/20 hover:shadow-orange-500/30'
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </button>
            ))}
            {/* Sign Up / Login Button */}
            <button
              onClick={() => setShowAuthModal(true)}
              className="dynamic-button px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium text-sm hover:from-cyan-600 hover:to-blue-700 transition-all duration-300 shadow-md shadow-cyan-500/20 hover:shadow-lg hover:shadow-cyan-500/30 flex items-center gap-1"
              data-testid="signup-login-button"
            >
              <UserPlus className="w-4 h-4" />
              Sign Up
            </button>
            {onOpenClientPortal && (
              <button
                onClick={onOpenClientPortal}
                className="dynamic-button px-4 py-2 rounded-lg bg-gradient-to-r from-slate-600 to-slate-700 text-white font-medium text-sm hover:from-slate-700 hover:to-slate-800 transition-all duration-300 shadow-md shadow-slate-500/20 hover:shadow-lg hover:shadow-slate-500/30 flex items-center gap-1"
                data-testid="client-login-button"
              >
                <User className="w-4 h-4" />
                My Account
              </button>
            )}
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className={`p-2 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg ${
                isDarkMode 
                  ? 'bg-gradient-to-r from-yellow-400 to-orange-400 text-gray-900 shadow-yellow-500/20 hover:shadow-yellow-500/30' 
                  : 'bg-gradient-to-r from-slate-700 to-slate-800 text-white shadow-slate-500/20 hover:shadow-slate-500/30'
              }`}
              data-testid="dark-mode-toggle"
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </nav>

          {/* Mobile Menu */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className={`${isDarkMode ? 'text-yellow-400 hover:text-yellow-300' : 'text-lime-500 hover:text-lime-600'} hover:bg-opacity-20`}>
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className={`${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'} w-[300px]`}>
              <div className="flex flex-col gap-4 mt-12">
                {/* Dark Mode Toggle - Mobile */}
                <button
                  onClick={toggleDarkMode}
                  className={`px-4 py-3 rounded-lg flex items-center gap-2 font-medium text-lg transition-all duration-300 ${
                    isDarkMode 
                      ? 'bg-gradient-to-r from-yellow-400 to-orange-400 text-gray-900' 
                      : 'bg-gradient-to-r from-slate-700 to-slate-800 text-white'
                  }`}
                >
                  {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                  {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </button>
                
                {navItems.map((item) => (
                  <button
                    key={item.label}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                    onClick={() => scrollToSection(item.href)}
                    className={`px-4 py-3 rounded-lg text-white font-medium text-lg transition-all duration-300 text-left ${
                      item.label === 'Industries'
                        ? 'bg-gradient-to-r from-rose-600 to-red-700 hover:from-rose-700 hover:to-red-800'
                        : 'bg-gradient-to-r from-orange-400 to-amber-500 hover:from-orange-500 hover:to-amber-600'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
                {/* Sign Up / Login - Mobile */}
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setShowAuthModal(true);
                  }}
                  className="px-4 py-3 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium text-lg hover:from-cyan-600 hover:to-blue-700 transition-all duration-300 text-left flex items-center gap-2"
                  data-testid="mobile-signup-login-button"
                >
                  <UserPlus className="w-5 h-5" />
                  Sign Up / Login
                </button>
                {onOpenClientPortal && (
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      onOpenClientPortal();
                    }}
                    className="px-4 py-3 rounded-lg bg-gradient-to-r from-slate-600 to-slate-700 text-white font-medium text-lg hover:from-slate-700 hover:to-slate-800 transition-all duration-300 text-left flex items-center gap-2"
                  >
                    <User className="w-5 h-5" />
                    My Account
                  </button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLoginSuccess={handleAuthSuccess}
      />
    </header>
  );
};

export default Header;
